import React from 'react';

/**
 * Composant Button accessible (WCAG 2.1 Level AA)
 * 
 * Améliorations d'accessibilité :
 * - aria-label pour boutons icônes uniquement
 * - aria-disabled au lieu de disabled natif (meilleure compatibilité lecteurs d'écran)
 * - Focus visible avec ring
 * - Taille de zone cliquable minimum 44x44px (WCAG 2.5.5)
 * - Indicateur de chargement accessible
 */
const Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md',
  disabled = false,
  fullWidth = false,
  icon: Icon,
  type = 'button',
  className = '',
  ariaLabel, // 🆕 Support aria-label explicite
  isLoading = false, // 🆕 État de chargement
  ...props // 🆕 Autres props HTML
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    primary: 'bg-blue-600 dark:bg-blue-700 text-white hover:bg-blue-700 dark:hover:bg-blue-800 focus:ring-blue-500',
    secondary: 'bg-gray-600 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-800 focus:ring-gray-500',
    success: 'bg-green-600 dark:bg-green-700 text-white hover:bg-green-700 dark:hover:bg-green-800 focus:ring-green-500',
    danger: 'bg-red-600 dark:bg-red-700 text-white hover:bg-red-700 dark:hover:bg-red-800 focus:ring-red-500',
    warning: 'bg-yellow-600 dark:bg-yellow-700 text-white hover:bg-yellow-700 dark:hover:bg-yellow-800 focus:ring-yellow-500',
    outline: 'border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-gray-500',
    ghost: 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-gray-500'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm min-h-[36px]', // 🆕 min-height pour accessibilité
    md: 'px-4 py-2.5 text-base min-h-[44px]', // 🆕 44px minimum (WCAG)
    lg: 'px-6 py-3 text-lg min-h-[48px]'
  };

  const widthClass = fullWidth ? 'w-full' : '';

  // 🆕 Déterminer le label accessible
  // Si bouton icône uniquement (pas de children), aria-label est REQUIS
  const isIconOnly = Icon && !children;
  const accessibleLabel = ariaLabel || (isIconOnly ? 'Action button' : undefined);

  return (
    <button
      type={type}
      onClick={disabled || isLoading ? undefined : onClick}
      disabled={disabled || isLoading}
      aria-label={accessibleLabel}
      aria-disabled={disabled || isLoading}
      aria-busy={isLoading}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`}
      {...props}
    >
      {/* 🆕 Indicateur de chargement */}
      {isLoading && (
        <svg 
          className="animate-spin -ml-1 mr-2 h-5 w-5" 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      
      {/* 🆕 Icône avec aria-hidden (car décorative) */}
      {Icon && !isLoading && (
        <Icon 
          size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} 
          className={children ? 'mr-2' : ''} 
          aria-hidden="true"
        />
      )}
      
      {children}
      
      {/* 🆕 Texte caché pour lecteurs d'écran (si aria-label fourni) */}
      {isIconOnly && ariaLabel && (
        <span className="sr-only">{ariaLabel}</span>
      )}
    </button>
  );
};

export default Button;
